import { UrlUtility } from '@ikea-aoa/ikea-shared-utils';
import {
  TAC_ADD_ITEM,
  TAC_ADD_MULTIPLE,
  TAC_REMOVE_ITEM,
  TAC_LOAD,
  TAC_UPDATE_ITEM,
  TAC_HIDE_ITEM,
  TAC_UPDATE_MULTIPLE,
  TAC_SET_WALL,
  TAC_SET_USE_MOUNTING_RAIL,
  TAC_REMOVE_TOAST_FLAGS,
  TAC_SET_TOAST_FLAGS,
  TAC_UPDATE_WALL_SIZE,
} from '../actionConstants';
import history from '../../services/history';
import { openDeeplinkChoice } from '../dialog/dialogActions';
import { actionVpcLoaded } from '../vpc/vpcActions';
import store from '..';
import pacValid from '../../services/PacValidator';
import fixOldVpc from '../../services/FixVPC';
import platform from '../../util/platform';
import constants from '../../settings/constants';
import storage from '../../services/history/storage';
import localStatisticsActions from '../../services/statistics/insights/custom/local/localStatisticsActions';
import { DesignSourceEnum } from '@insights/insights-data-provider';
import { thunkLoadPac, thunkOpenPacMissing } from './tacThunks';
import { thunkSetView } from '../navigation/navigationThunks';
import localStatisticsReporter from '../../services/statistics/insights/custom/local/localStatisticsReporter';
import mandatoryStatisticsReporter from '../../services/statistics/insights/mandatory/mandatoryStatisticsReporter';
import { getVpcService } from '../../services/ServiceHandler';

export const thunkNavigateWithMissingPac = () => dispatch => {
  dispatch(thunkSetView(constants.VIEW_NAMES.START));
  dispatch(thunkOpenPacMissing());
};

const reportMandatoryInitPlanningSession = (vpcCode, vpcSource) => {
  mandatoryStatisticsReporter.reportInitialPlanningSession(vpcCode, vpcSource);
};

const reportLocalOpenPlannerEvent = (entry, ...rest) => {
  localStatisticsReporter.reportOpenPlanner(entry, ...rest);
  if (UrlUtility.getCombinedQuery().applications) {
    localStatisticsReporter.reportSeriesGalleryUsage();
  }
};

const thunkHandleVpcCase = (vpcCode, saveGame) => dispatch => {
  // Check if "vpcSource=skytta" is present in url
  const hashQueryVpcSource = UrlUtility.getCombinedQuery().vpcSource;
  const designSource =
    hashQueryVpcSource && hashQueryVpcSource.includes(DesignSourceEnum.skytta)
      ? DesignSourceEnum.skytta
      : DesignSourceEnum.gallery;

  // Clean url from vpc code
  document.location.hash = document.location.hash.replace(
    /(.*)(?:#)((?:\/U\/)?.{5,6})(?:[&?]|$)(.*)/i,
    '$1#?$3'
  );
  /* // Clean url from initial view
  document.location.hash = document.location.hash.replace(
    /&[a-zA-Z]+_[a-zA-Z]+=[a-zA-Z]+/i,
    ''
  ); */
  return getVpcService()
    .getConfiguration(vpcCode)
    .then(data => {
      reportMandatoryInitPlanningSession(vpcCode, designSource);
      reportLocalOpenPlannerEvent(localStatisticsActions.VPC_DEEPLINK, vpcCode);

      dispatch(
        actionVpcLoaded(vpcCode, store.getState().navigation.currentView)
      );
      return fixOldVpc(data.configuration);
    })
    .catch(error => {
      localStatisticsReporter.reportOpenPlannerVpcDeeplinkNotSupported(vpcCode);
      // didnt find vpc, so if savegame present,
      // let user close gallery and resume
      dispatch(thunkNavigateWithMissingPac());
      return saveGame;
    });
};

const thunkHandleSprCase = (sprId, saveGame) => dispatch => {
  const sprList = store.getState().sprs;
  const spr = sprList.find(
    spr => spr.id === sprId || spr.kompisSPR.content?.ruItemNo === sprId
  );

  if (!spr) {
    localStatisticsReporter.reportOpenPlannerSprDeeplinkNotSupported(sprId);
  } else {
    reportMandatoryInitPlanningSession(spr?.vpcCode, DesignSourceEnum.spr);
    reportLocalOpenPlannerEvent(localStatisticsActions.SPR_DEEPLINK, sprId);
  }

  if (spr && (!saveGame || !pacValid(saveGame))) {
    // Deeplinked SPR and nothing in storage so go with the SPR.
    return Promise.resolve(spr);
  } else if (spr && saveGame) {
    // We have something stored so user must decide if this is to be
    // discarded and use deeplinked SPR. We ask the user, but load the
    // stored configuration for now.
    dispatch(openDeeplinkChoice({ spr }));
    return Promise.resolve(saveGame);
  } else if (!spr && saveGame) {
    // SPR not found. Inform the user, go to the closable SPR gallery and
    // load the "save game" so that something is there if the usr closes
    // the SPR gallery.
    dispatch(thunkNavigateWithMissingPac());
    return Promise.resolve(saveGame);
  } else {
    dispatch(thunkNavigateWithMissingPac());
    return Promise.resolve();
  }
};

const thunkHandleSaveGameCase = saveGame => dispatch => {
  reportMandatoryInitPlanningSession('', DesignSourceEnum.startFromTemplate);
  reportLocalOpenPlannerEvent(localStatisticsActions.SAVE);

  const promise = Promise.resolve(saveGame);
  const isLangPickerReload = storage.session.getItem(
    constants.SESSION_STORAGE.LANGUAGE_PICKER_ACTIVE_VIEW_KEY
  );

  if (platform.isKiosk && !isLangPickerReload) {
    dispatch(thunkSetView(constants.VIEW_NAMES.START));
  }

  return promise;
};

export function findPAC() {
  return dispatch => {
    const vpcMatch = /(?:(?:#|\/U\/))(\w{5,6})(?=[&?]|$)/i.exec(
      document.location.hash
    );
    const vpcCode = vpcMatch && vpcMatch[1];

    // example spr id 79272677
    const sprMatch = /(?:#S)(\d{8})(?=[&?]|$)/.exec(document.location.hash);
    const sprId = sprMatch && sprMatch[1];
    const saveGame = history.load();

    const handleCase = () => {
      if (vpcCode) return dispatch(thunkHandleVpcCase(vpcCode));
      else if (sprId) return dispatch(thunkHandleSprCase(sprId, saveGame));
      else if (saveGame) return dispatch(thunkHandleSaveGameCase(saveGame));
      else {
        reportMandatoryInitPlanningSession(
          '',
          DesignSourceEnum.startFromScratch
        );
        reportLocalOpenPlannerEvent(localStatisticsActions.NO_SAVE);

        return Promise.reject(new Error('No pacs found'));
      }
    };

    return handleCase()
      .then(pac => {
        if (!pac) {
          // a dialog took care of it
          return;
        } else if (pacValid(pac)) {
          dispatch(thunkLoadPac(pac));
        } else {
          dispatch(thunkNavigateWithMissingPac());
        }
      })
      .catch(e => {
        dispatch(thunkSetView(constants.VIEW_NAMES.START));
      });
  };
}

export const actionLoadPac = (model, errors, centerConfiguration) => ({
  type: TAC_LOAD,
  payload: {
    model,
    centerConfiguration,
    errors,
  },
});

const genericTacUpdaterAction =
  type =>
  (model, errors, meta = {}) => ({
    type,
    payload: {
      model,
      errors,
    },
    meta,
  });

export const actionAddItem = genericTacUpdaterAction(TAC_ADD_ITEM);
export const actionAddMultiple = genericTacUpdaterAction(TAC_ADD_MULTIPLE);
export const actionRemoveItem = genericTacUpdaterAction(TAC_REMOVE_ITEM);
export const actionHideItem = genericTacUpdaterAction(TAC_HIDE_ITEM);
export const actionUpdateItem = genericTacUpdaterAction(TAC_UPDATE_ITEM);
export const actionUpdateMultiple =
  genericTacUpdaterAction(TAC_UPDATE_MULTIPLE);
export const actionSetWall = genericTacUpdaterAction(TAC_SET_WALL);

export function tacSetToastFlags(toastFlags) {
  return {
    type: TAC_SET_TOAST_FLAGS,
    payload: {
      toastFlags,
    },
  };
}

export function actionSetUseMountingRails(model) {
  return {
    type: TAC_SET_USE_MOUNTING_RAIL,
    payload: {
      model,
    },
  };
}

export function removeToastFlags() {
  return {
    type: TAC_REMOVE_TOAST_FLAGS,
  };
}

export const actionSetWallSize = wallSize => ({
  type: TAC_UPDATE_WALL_SIZE,
  payload: wallSize,
});
